





















































































import Vue from 'vue';
import {
    computed,
    defineComponent,
    PropType,
} from '@vue/composition-api';
import EventCardTitle from './EventCardTitle.vue';
import Event from './Event.vue';
import EventCardDivider from './EventCardDivider.vue';
import OddOfferForExtendedEventCard from './OddOfferForExtendedEventCard.vue';
import OfferEvent from '@core/models/offer/OfferEvent';
import Market from '@core/models/offer/Market';
import { EventCardSource } from '@app/enums';
import { OddValueDynamics } from '@store/modules/data/sportOffer/types';
import { useVuexStore } from '@superbet-group/web.lib.vue-utilities';
import PreferencesStore from '@store/modules/ui/user/preferences/preferencesIndex';
import { AnalyticsProtoEvents } from '@src/app/utils/analyticsScreenNames';
import { SportOfferStore } from '@store/modules/ui/sportOffer/sportPage/sportPageIndex';
import { useEventObserver } from '@shared/composables/feedPersonalizationAnalytics/useEventObserver';
import {
    ClickEventType,
    TestClickEventTypes,
} from '@src/app/utils/clickAnalytics';
import ExtendedEventCardHorizontalScroll from '@shared/components/ExtendedEventCardHorizontalScroll.vue';
import { SportOfferMarketsStore } from '@store/modules/ui/sportOffer/markets';
import { getConfig } from '@superbet-group/web.lib.config';
import { NON_FINAL_MAX_ODDS } from '@core/services/offerPresentation/OfferPresentationService';
import { Analytics as AnalyticsSDK } from '@superbet-group/web.analytics.sdk';
import { ClickName } from '@superbet-group/product.analytics';
import EventBettorNumber from './EventBettorNumber.vue';
import { NavigationDataStore } from '@store/modules/navigation';
import {
    PeerUrgencyExperimentVariant,
    PE131DescriptiveOddNameVariant,
} from '@core/models/user/flags';
import { getPe131DescriptiveOddButtonVariant } from '@app/shared/featureFlags/pe131DescriptiveOddButtonFlag';

export default defineComponent({
    name: 'EventCard',
    emits: [
        'pinClick',
        'oddClick',
    ],
    props: {
        event: {
            type: Object as PropType<OfferEvent>,
            required: true,
        },
        expandedEvent: {
            type: Number,
            default: null,
        },
        isForceVerticalAligned: {
            type: Boolean,
            required: false,
            default: false,
        },
        market: {
            type: Object as PropType<Market | null>,
            default: null,
        },
        oddValueDynamics: {
            type: Object as PropType<OddValueDynamics>,
            required: false,
            default: undefined,
        },
        selectedOdds: {
            type: Set as PropType<Set<string>>,
            default: () => new Set(),
        },
        isPrimaryMarketSelected: {
            type: Boolean,
            default: true,
        },
        isPinShown: {
            type: Boolean,
            default: true,
        },
        showTitle: {
            type: Boolean,
            default: false,
        },
        eventCardSource: {
            type: String as PropType<EventCardSource>,
            required: true,
        },
        bettorNumber: {
            type: Number as PropType<number>,
            required: false,
            default: -1,
        },
        socialProofDesignVariant: {
            type: String as PropType<PeerUrgencyExperimentVariant>,
            required: false,
            default: PeerUrgencyExperimentVariant.None,
        },
    },
    setup(props, { emit }) {
        const navigationStore = useVuexStore<NavigationDataStore>('navigation');
        const preferencesStore = useVuexStore<typeof PreferencesStore>('ui/user/preferences');
        const sportOfferStore = useVuexStore<SportOfferStore>('ui/sportOffer');
        const marketsStore = useVuexStore<SportOfferMarketsStore>('ui/sportOffer/markets');

        const nonFinalMarketsConfig = marketsStore.state('nonFinalMarketsConfig');
        const activeQuickFilter = sportOfferStore.getter('activeQuickFilter');

        const { observedElementRef, visibleEvents } = useEventObserver();

        const teams = computed(() => {
            return {
                homeTeam: props.event.team1Name,
                awayTeam: props.event.team2Name,
            };
        });

        function pinClick() {
            emit('pinClick', props.event.id);
        }

        const nonFinalMarketIdsForCurrentSport = computed(() => {
            const sportId = props.event.sportId;

            const country = getConfig().countrySpecific.country.toUpperCase();

            const countryOverride = nonFinalMarketsConfig.value?.overrides[country]?.[sportId];

            const defaultConfig = nonFinalMarketsConfig.value?.default[sportId];

            return countryOverride || defaultConfig || [];
        });

        const getNextMarket = (markets: Market[]) => markets.slice(1).find((market) => market.odds.length < NON_FINAL_MAX_ODDS);

        const secondMarket = computed(() => {
            const marketIds = nonFinalMarketIdsForCurrentSport.value;
            const eventMarkets = props.event.getMarkets();

            if (!marketIds.length) {
                return getNextMarket(eventMarkets);
            }

            let foundMarket;

            for (let i = 0; i < marketIds.length; i++) {
                const el = marketIds[i];
                const id = typeof el === 'number' ? el : el.id;
                const market = eventMarkets.find((market) => market.id === id);

                if (market) {
                    foundMarket = market;
                    break;
                }
            }

            return foundMarket || getNextMarket(eventMarkets);
        });

        const handleClick = (event: MouseEvent) => {
            // clicking on odd doesnt open match details page
            if ((event.target as Element).className?.includes('odd-button')) { return; }
            // @TODO(corneliu-iancu): Temporary dual analytics implementation.
            // We are currently in the process of migrating from $superAnalytics to AnalyticsSDK.
            // Both libraries are being used in parallel to validate data consistency between them.
            // DO NOT modify or remove calls to $superAnalytics until the migration is fully validated and approved.
            // Once validation is complete, $superAnalytics will be deprecated and removed.
            AnalyticsSDK.getInstance().log('click', {
                click_name: ClickName.CLICK_NAME_UNSPECIFIED,
                click: {
                    click_name: 'feed_personalisation_match_details_click',
                    click_value_1: props.event.uuid,
                    click_value_2: visibleEvents.value.filter((id) => id !== props.event.uuid).toString(),
                    click_value_3: activeQuickFilter.value,
                    type: 'test_click',
                },
            });
            Vue.prototype.$superAnalytics.sendEvent({
                AnalyticsEvent: AnalyticsProtoEvents.click,
                eventType: AnalyticsProtoEvents.click,
                [AnalyticsProtoEvents.click]: {
                    clickName: ClickEventType.clickNameUnspecified,
                    clickType: 'test_click',
                    testClick: {
                        clickName: TestClickEventTypes.feedPersonalizationMatchDetailsClick,
                        clickValue_1: {
                            value: props.event.uuid,
                        },
                        clickValue_2: {
                            value: visibleEvents.value.filter((id) => id !== props.event.uuid).toString(),
                        },
                        clickValue_3: {
                            value: activeQuickFilter.value,
                        },
                    },
                },
            });
        };

        return {
            isOnHomePage: navigationStore.getter('isOnHomePage'),
            teams,
            isEventPinned: computed(() => {
                return preferencesStore.getter('pinnedEventIds').value.includes(props.event.id);
            }),
            pinClick,
            observedElementRef,
            handleClick,
            secondMarket,
            applyPe131StyleChanges: getPe131DescriptiveOddButtonVariant() !== PE131DescriptiveOddNameVariant.None,
        };
    },
    components: {
        EventCardTitle,
        Event,
        EventCardDivider,
        OddOfferForExtendedEventCard,
        ExtendedEventCardHorizontalScroll,
        EventBettorNumber,
    },
});
